import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Wrench } from "@phosphor-icons/react";

export default function ReportIssue() {
  const [name, setName] = useState("");
  const [area, setArea] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error("Please describe the issue");
      return;
    }
    setSending(true);
    try {
      await api.post("/maintenance", { guest_name: name, area, message });
      setDone(true);
      setName("");
      setArea("");
      setMessage("");
      toast.success("Thank you! Your report has been sent to the owner.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send report");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="report-issue"
      className="scroll-mt-24 px-6 sm:px-10 lg:px-16 py-20 md:py-28 bg-secondary/50 border-b border-border"
      data-testid="section-report-issue"
    >
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="inline-flex items-center gap-2 text-accent mb-4">
            <Wrench size={22} weight="duotone" />
            <span className="subhead text-accent text-sm">Something not right?</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-[0.95] text-foreground">
            Report a maintenance issue
          </h2>
          <p className="text-base md:text-lg leading-relaxed text-foreground/75 mt-5">
            If anything isn't working quite right — a dripping faucet, a remote that won't
            cooperate, a light that's out — please tell us. It's never a bother. Flagging it
            helps us fix it fast for you and for the guests who follow, and we'd be genuinely
            grateful that you took the time. Thank you.
          </p>

          {done ? (
            <div
              className="mt-10 border border-accent/40 bg-background p-8 text-center"
              data-testid="report-thanks"
            >
              <p className="font-display text-2xl text-foreground">Thank you so much.</p>
              <p className="text-muted-foreground mt-2">
                We've received your note and truly appreciate it.
              </p>
              <button
                onClick={() => setDone(false)}
                data-testid="report-another-btn"
                className="mt-6 text-sm text-accent link-underline"
              >
                Report another issue
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-10 space-y-6" data-testid="report-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="subhead text-foreground/50 text-xs">Your name (optional)</label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="report-name"
                    className="rounded-none border-0 border-b border-foreground/25 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-accent"
                  />
                </div>
                <div className="space-y-2">
                  <label className="subhead text-foreground/50 text-xs">Area / item (optional)</label>
                  <Input
                    value={area}
                    onChange={(e) => setArea(e.target.value)}
                    placeholder="e.g. Kitchen, Pool, TV"
                    data-testid="report-area"
                    className="rounded-none border-0 border-b border-foreground/25 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-accent"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="subhead text-foreground/50 text-xs">What's going on?</label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Describe the issue in a sentence or two…"
                  data-testid="report-message"
                  className="rounded-none border border-border bg-transparent focus-visible:ring-accent min-h-[120px]"
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="report-submit-btn"
                className="inline-flex items-center gap-3 bg-foreground text-primary-foreground pl-8 pr-6 py-4 rounded-full hover:bg-accent transition-colors duration-300 disabled:opacity-60"
              >
                <span className="text-sm font-semibold tracking-wide">
                  {sending ? "Sending…" : "Send to the owner"}
                </span>
                <Wrench size={18} weight="bold" />
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
